// src/components/Hero3.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { keyframes } from "@emotion/react";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ChildCareRoundedIcon from "@mui/icons-material/ChildCareRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import SurfingRoundedIcon from "@mui/icons-material/SurfingRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

// ✅ lightbox icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import lobos1 from "../images/lobos1.jpg";
import lobos2 from "../images/lobos2.jpg";
import lobos3 from "../images/lobos3.jpg";
import lobos4 from "../images/lobos4.jpg";
import loboslogo from "../images/loboslogo.jpg";

const MotionBox = motion(Box);

const SURF = {
  name: "Lobos del Pacífico",
  whatsappNumber: "56997188391",
  whatsappMsg: "Hola Lobos del Pacífico 👋 Vengo de parte de Bichon. Quiero consultar horarios y clases de surf.",
  instagramUrl: "https://www.instagram.com/lobosdelpacificoescueladesurf/",
  facebookUrl: "https://www.facebook.com/lobillospichilemu",
  mapsUrl:
    "https://www.google.com/maps/place/Lobos+del+Pacifico+-+Surf+Club/@-34.3850769,-72.0145395,17z/data=!3m1!4b1!4m6!3m5!1s0x966159e832da32a5:0xe3ca2b92d011c208!8m2!3d-34.3850769!4d-72.0119646!16s%2Fg%2F11x2jr8pds?entry=ttu",
  addressLine: "Av. Costanera 730, Pichilemu.",
  mapQuery:
    "Lobos del Pacifico - Surf Club, Av. Costanera 730, Pichilemu, O'Higgins, Chile",
};

export default function Hero3() {
  const gradientShift = useMemo(
    () => keyframes`
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    `,
    [],
  );

  const wsp = useMemo(() => {
    const base = `https://wa.me/${SURF.whatsappNumber}`;
    const text = encodeURIComponent(SURF.whatsappMsg);
    return `${base}?text=${text}`;
  }, []);

  // ✅ botones más compactos
  const primaryCtaSx = useMemo(
    () => ({
      borderRadius: 2,
      px: { xs: 2.4, sm: 2.6 },
      py: { xs: 0.95, sm: 1.0 },
      minHeight: 40,
      fontSize: { xs: "0.92rem", sm: "0.94rem" },
      fontWeight: 900,
      textTransform: "none",
      color: "#fff",
      boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
      background: "linear-gradient(270deg, #49d0b0, #16a357, #c72e64, #cfc54a)",
      backgroundSize: "700% 700%",
      animation: `${gradientShift} 3.4s ease infinite`,
      transition: "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 14px 30px rgba(0,0,0,0.24)",
        filter: "brightness(1.05)",
      },
    }),
    [gradientShift],
  );

  const outlinedSx = (color) => ({
    borderRadius: 2,
    fontWeight: 900,
    textTransform: "none",
    color,
    borderColor: color,
    px: { xs: 2.2, sm: 2.4 },
    py: { xs: 0.9, sm: 0.95 },
    minHeight: 40,
    fontSize: { xs: "0.92rem", sm: "0.94rem" },
    transition: "transform 220ms ease, background-color 220ms ease",
    "&:hover": {
      borderColor: color,
      backgroundColor: `${color}14`,
      transform: "translateY(-1px)",
    },
  });

  const bullets = [
    { icon: <SchoolRoundedIcon />, color: "#49d0b0", text: "Escuela de surf para todas las edades." },
    { icon: <ChildCareRoundedIcon />, color: "#cfc54a", text: "Clases para niños y adultos." },
    { icon: <GroupsRoundedIcon />, color: "#16a357", text: "Modalidad grupal e individual." },
    { icon: <AccessTimeRoundedIcon />, color: "#c72e64", text: "Varias clases por día — horarios por WhatsApp." },
    { icon: <SurfingRoundedIcon />, color: "#27276d", text: "Arriendo de equipo: trajes, tablas, leash y más." },
  ];

  const gallery = [lobos1, lobos2, lobos3, lobos4];

  const MAP_Q = encodeURIComponent(SURF.mapQuery);
  const MAP_SRC = `https://www.google.com/maps?q=${MAP_Q}&z=16&hl=es&output=embed`;

  // ✅ LIGHTBOX STATE
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const openLightbox = useCallback((idx) => {
    setActiveIdx(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const prev = useCallback(() => {
    setActiveIdx((i) => (i - 1 + gallery.length) % gallery.length);
  }, [gallery.length]);

  const next = useCallback(() => {
    setActiveIdx((i) => (i + 1) % gallery.length);
  }, [gallery.length]);

  // ✅ teclado: ESC / flechas
  useEffect(() => {
    if (!lightboxOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen, closeLightbox, prev, next]);

  return (
    <Box
      component="section"
      id="experiencias"
      sx={{
        position: "relative",
        minHeight: { xs: "auto", md: "70vh" },
        display: "flex",
        alignItems: "center",
        py: { xs: 4, md: 4 },
        overflow: "hidden",
        bgcolor: "#27276d",
      }}
    >
      {/* Fondo */}
      <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 18% 20%, rgba(73,208,176,0.55), transparent 48%)," +
              "radial-gradient(circle at 80% 25%, rgba(199,46,100,0.26), transparent 52%)," +
              "radial-gradient(circle at 55% 80%, rgba(207,197,74,0.22), transparent 52%)," +
              "radial-gradient(circle at 10% 85%, rgba(22,163,87,0.18), transparent 58%)," +
              "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0))",
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {/* IZQ */}
          <MotionBox
            component={Paper}
            elevation={0}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            sx={{
              // ✅ menos padding = menos alto total
              p: { xs: 2.6, md: 3.2 },
              borderRadius: 3,
              background: "rgba(255,255,255,0.96)",
              border: "1px solid rgba(255,255,255,0.18)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack direction="row" spacing={1.6} alignItems="center">
              <Box
                component="img"
                src={loboslogo}
                alt="Lobos del Pacífico"
                loading="eager"
                decoding="async"
                draggable={false}
                sx={{
                  // ✅ logo un toque más chico
                  width: { xs: 56, sm: 60, md: 66 },
                  height: { xs: 56, sm: 60, md: 66 },
                  borderRadius: 3,
                  objectFit: "cover",
                  boxShadow: "0 12px 22px rgba(0,0,0,0.18)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  bgcolor: "#fff",
                  flexShrink: 0,
                }}
              />

              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: "#c72e64",
                    fontWeight: 900,
                    letterSpacing: 2,
                    fontFamily: '"Inter"',
                    lineHeight: 1,
                  }}
                >
                  Escuela de surf
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: "#27276d",
                    fontFamily: '"DynaPuff", cursive',
                    lineHeight: 1.05,
                    mt: 0.15,
                    fontSize: { xs: "1.55rem", md: "1.7rem" }, // ✅ un toque más chico
                  }}
                >
                  {SURF.name}
                </Typography>
              </Box>
            </Stack>

            {/* ✅ frase más chica y con menos margen */}
            <Typography
              sx={{
                py: 2,
                color: "rgba(0,0,0,0.70)",
                fontWeight: 650,
                fontSize: { xs: "0.95rem", md: "0.98rem" },
                lineHeight: 1.25,
              }}
            >
              Clases para aprender o mejorar, arriende de equipo por día o por sesión.
            </Typography>

            {/* ✅ bullets más compactos */}
            <List dense sx={{ mt: 0.8, py: 0 }}>
              {bullets.map((b) => (
                <ListItem key={b.text} sx={{ px: 0, py: 0.28 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: b.color }}>{b.icon}</ListItemIcon>
                  <ListItemText
                    primary={b.text}
                    primaryTypographyProps={{
                      sx: {
                        color: "rgba(0,0,0,0.75)",
                        fontWeight: 650,
                        fontSize: "0.95rem",
                        lineHeight: 1.2,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>

            {/* ✅ chips en small y con menos aire */}
            <Stack direction="row" spacing={0.8} sx={{ mt: 1.05, flexWrap: "wrap" }}>
              <Chip
                size="small"
                icon={<CheckCircleRoundedIcon />}
                label="Niños"
                sx={{ fontWeight: 900, bgcolor: "rgba(73,208,176,0.14)", height: 28 }}
              />
              <Chip
                size="small"
                icon={<PersonRoundedIcon />}
                label="Adultos"
                sx={{ fontWeight: 900, bgcolor: "rgba(199,46,100,0.12)", height: 28 }}
              />
              <Chip
                size="small"
                icon={<GroupsRoundedIcon />}
                label="Grupales"
                sx={{ fontWeight: 900, bgcolor: "rgba(207,197,74,0.18)", height: 28 }}
              />
              <Chip
                size="small"
                icon={<PersonRoundedIcon />}
                label="Individuales"
                sx={{ fontWeight: 900, bgcolor: "rgba(22,163,87,0.12)", height: 28 }}
              />
            </Stack>

            {/* ✅ divisor con menos margen */}
            <Divider sx={{ my: 3 }} />

            <Stack spacing={0.9}>
              {/* ✅ dirección más chica + IG/FB como iconos (ahorra 1 fila entera de botones) */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0,}}>
                  <PlaceOutlinedIcon sx={{ color: "#49d0b0", }} />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: "rgba(0,0,0,0.72)",
                      fontSize: "0.95rem",
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={SURF.addressLine}
                  >
                    {SURF.addressLine}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.4}>
                  <IconButton
                    size="small"
                    onClick={() => window.open(SURF.instagramUrl, "_blank")}
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      border: "1px solid rgba(199,46,100,0.35)",
                      color: "#c72e64",
                      "&:hover": { bgcolor: "rgba(199,46,100,0.10)" },
                    }}
                  >
                    <InstagramIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => window.open(SURF.facebookUrl, "_blank")}
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      border: "1px solid rgba(73,208,176,0.40)",
                      color: "#49d0b0",
                      "&:hover": { bgcolor: "rgba(73,208,176,0.12)" },
                    }}
                  >
                    <FacebookRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* ✅ CTAs ocupan menos alto */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9} >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={primaryCtaSx}
                  href={wsp}
                  target="_blank"
                  rel="noopener"
                >
                  Consultar por WhatsApp
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlaceOutlinedIcon />}
                  sx={outlinedSx("#27276d")}
                  onClick={() => window.open(SURF.mapsUrl, "_blank")}
                >
                  Cómo llegar
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ flex: 1 }} />
          </MotionBox>

          {/* DER (sin cambios) */}
          <Stack spacing={2} sx={{ height: "100%" }}>
            <MotionBox
              component={Paper}
              elevation={0}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, ease: "easeOut", delay: 0.06 }}
              sx={{
                p: 0,
                borderRadius: 3,
                overflow: "hidden",
                background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(255,255,255,0.18)",
                height: { xs: 260, md: 320 },
              }}
            >
              <Box
                component="iframe"
                title="Mapa Lobos del Pacífico"
                width="100%"
                height="100%"
                frameBorder="0"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={MAP_SRC}
                sx={{ display: "block" }}
              />
            </MotionBox>

            <MotionBox
              component={Paper}
              elevation={0}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(255,255,255,0.18)",
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  color: "#27276d",
                  fontFamily: '"DynaPuff", cursive',
                  mb: 1.2,
                }}
              >
                Galería de fotos
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                  gap: { xs: 1.2, md: 1.4 },
                }}
              >
                {gallery.map((src, idx) => (
                  <Box
                    key={idx}
                    role="button"
                    tabIndex={0}
                    onClick={() => openLightbox(idx)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openLightbox(idx);
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 2,
                      overflow: "hidden",
                      aspectRatio: "1 / 1",
                      boxShadow: "0 12px 22px rgba(0,0,0,0.14)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      transition: "transform 180ms ease, filter 180ms ease",
                      "&:hover": { transform: "scale(1.03)", filter: "saturate(1.12)" },
                      "&:focus-visible": { outline: "3px solid rgba(73,208,176,0.45)", outlineOffset: 2 },
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt={`Lobos ${idx + 1}`}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </MotionBox>
          </Stack>
        </Box>
      </Container>

      {/* LIGHTBOX (igual) */}
      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "rgba(10,10,20,0.92)",
            backdropFilter: "blur(12px)",
          },
        }}
      >
        <Box sx={{ position: "relative", p: { xs: 1.5, sm: 2 } }}>
          <IconButton
            onClick={closeLightbox}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              bgcolor: "rgba(255,255,255,0.16)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              top: "50%",
              left: 10,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255,255,255,0.16)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
            }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>

          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              top: "50%",
              right: 10,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255,255,255,0.16)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>

          <Box
            component="img"
            src={gallery[activeIdx]}
            alt={`Lobos grande ${activeIdx + 1}`}
            sx={{
              width: "100%",
              maxHeight: { xs: "70vh", md: "80vh" },
              objectFit: "contain",
              display: "block",
              borderRadius: 2,
            }}
          />

          <Typography
            sx={{
              mt: 1.2,
              color: "rgba(255,255,255,0.75)",
              fontWeight: 800,
              textAlign: "center",
            }}
          >
            {activeIdx + 1} / {gallery.length}
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
}
